import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { submitComment } from '../../services/commentService';

const Form = styled.form`
  margin-top: 2rem;
  padding: 1.25rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.card || theme.colors.background};
`;

const Title = styled.h3`
  margin: 0 0 1rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
`;

const ReplyBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary}12;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.foreground};
`;

const CancelReply = styled.button`
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const Textarea = styled.textarea`
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.foreground};
  font-size: 0.9375rem;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary}55;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SubmitBtn = styled.button`
  margin-top: 1rem;
  padding: 0.65rem 1.25rem;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground || '#fff'};
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.875rem;
  color: ${({ $error, theme }) => ($error ? theme.colors.destructive || '#dc2626' : theme.colors.mutedForeground)};
`;

const SignInPrompt = styled.div`
  margin-top: 2rem;
  padding: 1.25rem;
  border-radius: 12px;
  border: 1px dashed ${({ theme }) => theme.colors.border};
  text-align: center;
`;

const SignInText = styled.p`
  margin: 0 0 1rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: 0.9375rem;
`;

const SignInActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const AuthLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 0.55rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
`;

const PrimaryLink = styled(AuthLink)`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground || '#fff'};
`;

const SecondaryLink = styled(AuthLink)`
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.foreground};
`;

const SignedInAs = styled.p`
  margin: 0 0 1rem;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.mutedForeground};

  strong {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

export default function CommentForm({ articleId, onSubmitted, replyTo, onCancelReply }) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const location = useLocation();
  const { isAuthenticated, user } = useSelector((s) => s.auth);
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (replyTo) {
      setBody('');
      setFeedback(null);
      setIsError(false);
    }
  }, [replyTo]);

  if (!isAuthenticated) {
    return (
      <SignInPrompt id="comment-form">
        <Title>{replyTo ? t('comments.replyTitle') : t('comments.formTitle')}</Title>
        <SignInText>{t('comments.signInRequired')}</SignInText>
        <SignInActions>
          <PrimaryLink to="/login" state={{ from: location }}>
            {t('auth.signIn')}
          </PrimaryLink>
          <SecondaryLink to="/register" state={{ from: location }}>
            {t('auth.createAccount')}
          </SecondaryLink>
        </SignInActions>
      </SignInPrompt>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!articleId) return;

    setSubmitting(true);
    setFeedback(null);
    setIsError(false);

    try {
      const res = await submitComment(articleId, {
        body: body.trim(),
        language,
        parentId: replyTo?._id || null,
      });

      setFeedback(res.message || t('comments.pendingMessage'));
      setBody('');
      onCancelReply?.();
      onSubmitted?.();
    } catch (err) {
      setIsError(true);
      setFeedback(err?.response?.data?.message || t('comments.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} noValidate id="comment-form">
      <Title>{replyTo ? t('comments.replyTitle') : t('comments.formTitle')}</Title>
      {replyTo ? (
        <ReplyBanner>
          <span>{t('comments.replyingTo', { name: replyTo.authorName })}</span>
          <CancelReply type="button" onClick={onCancelReply}>
            {t('comments.cancelReply')}
          </CancelReply>
        </ReplyBanner>
      ) : null}
      <SignedInAs>
        {t('comments.commentingAs')} <strong>{user?.username}</strong>
      </SignedInAs>
      <Field>
        {t('comments.bodyLabel')}
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          required
          maxLength={2000}
          placeholder={t('comments.bodyPlaceholder')}
        />
      </Field>
      <SubmitBtn type="submit" disabled={submitting}>
        {submitting ? t('comments.submitting') : replyTo ? t('comments.replySubmit') : t('comments.submit')}
      </SubmitBtn>
      {feedback ? <Message $error={isError}>{feedback}</Message> : null}
    </Form>
  );
}
